import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import useTheme from '@mui/material/styles/useTheme';

import { SiteInfoResponseModelV2 } from '@newfold/huapi-js/src/index.schemas';

import ActionChip from '~/components/ActionChip';
import CardTopBanner from '~/components/CardTopBanner';
import { SEVERITY_COLORS } from '~/components/CardTopBanner/utils/constants';
import useAMDomainFlow from '~/hooks/useAMDomainFlow';
import { BannerConfig } from '~/hooks/useInfoBanners';
import { useSiteContext } from '~/scenes/Site';
import {
  checkWordPressType,
  isFreeTrialAiPlan,
  isFreeTrialAiSite,
} from '~/utils/site/helpers';

import { ActionChipConfig } from '../utils/types';

type SiteDomainActionsPropOptions = {
  siteInfoOverride: SiteInfoResponseModelV2;
  prodCode?: string | null;
  isDisabled?: boolean;
  isListView?: boolean;
  isAutogenPendingURL?: boolean;
  pendingDomainIsNotPointed?: boolean;
  sitesDomainLoading?: boolean;
  variant?: 'chip' | 'topcap';
  onShouldShowManageButton?: (shouldShow: boolean) => void;
  onPrepareHostingContext?: () => void;
};

const SiteDomainActions = ({
  siteInfoOverride,
  prodCode = undefined,
  isDisabled: isDisabledProp = undefined,
  isListView = false,
  isAutogenPendingURL = false,
  pendingDomainIsNotPointed = false,
  sitesDomainLoading = false,
  variant = 'topcap',
  onShouldShowManageButton = undefined,
  onPrepareHostingContext = undefined,
}: SiteDomainActionsPropOptions) => {
  const { t } = useTranslation('site');
  const theme = useTheme();
  const chipLinkLabelSx = {
    textTransform: 'lowercase',
    textDecoration: 'none',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.warning.dark,
    },
  };
  const navigate = useNavigate();
  const siteContext = useSiteContext();
  const siteInfoFromContext = siteContext?.data;
  const isChipVariant = variant === 'chip';
  // Use override if provided, otherwise use context
  const siteInfo = siteInfoOverride ?? siteInfoFromContext;
  const isFreeTrialAiHosting = prodCode
    ? isFreeTrialAiPlan(prodCode)
    : isFreeTrialAiSite(siteInfo);
  // Use prop if provided, otherwise fall back to context
  const isDisabled =
    isDisabledProp ?? siteContext?.isSiteOrHostingDisabled ?? false;

  // Use AM Domain Flow hook
  const { isAMDomainPointerFlow, domainFlowUrl } = useAMDomainFlow(siteInfo);

  const isWordPress = checkWordPressType({
    type: siteInfo?.type,
    detected_type: siteInfo?.detected_type,
  });

  // Show temporary domain alert when site is using provisional URL and no pending URL
  const shouldShowConnectDomainAlert =
    !siteInfo?.pending_url &&
    !!siteInfo?.url_is_provisional &&
    !isFreeTrialAiHosting;

  // Show finish setup alert - different conditions for list vs detail view
  const shouldShowFinishSetupAlert = isListView
    ? false
    : !!(
        siteInfo?.pending_url &&
        isWordPress &&
        !sitesDomainLoading &&
        (isAutogenPendingURL || pendingDomainIsNotPointed)
      );

  // Notify parent if manage button should be shown (when no alerts are shown)
  const shouldShowManageButton =
    !shouldShowConnectDomainAlert && !shouldShowFinishSetupAlert;
  useEffect(() => {
    if (onShouldShowManageButton) {
      onShouldShowManageButton(shouldShowManageButton);
    }
  }, [shouldShowManageButton, onShouldShowManageButton]);

  const handleConnectDomain = useCallback(() => {
    if (isDisabled) return;

    onPrepareHostingContext?.();

    if (isAMDomainPointerFlow && domainFlowUrl) {
      window.location.assign(domainFlowUrl);
    } else if (siteInfo?.id) {
      navigate(`/sites/${siteInfo.id}/domains/add`);
    }
  }, [
    isDisabled,
    isAMDomainPointerFlow,
    domainFlowUrl,
    navigate,
    onPrepareHostingContext,
    siteInfo?.id,
  ]);

  const handleFinishSetup = useCallback(() => {
    if (isDisabled || !siteInfo?.id) return;

    navigate(`/sites/${siteInfo.id}/domains`);
  }, [isDisabled, navigate, siteInfo?.id]);

  let alertType: 'connect' | 'finish' | null = null;
  if (shouldShowConnectDomainAlert) {
    alertType = 'connect';
  } else if (shouldShowFinishSetupAlert) {
    alertType = 'finish';
  }

  if (!alertType) return null;

  if (isChipVariant) {
    const chipConfig: ActionChipConfig =
      alertType === 'connect'
        ? {
            icon: (
              <LanguageIcon
                style={{
                  fontSize: 13,
                  color: theme.palette.warning.dark,
                }}
              />
            ),
            linkLabel: t('connectDomain'),
            linkSx: chipLinkLabelSx,
            onLinkClick: handleConnectDomain,
          }
        : {
            icon: (
              <ErrorOutlineIcon
                style={{
                  fontSize: 13,
                  color: theme.palette.warning.dark,
                }}
              />
            ),
            linkLabel: t('finishSetup'),
            linkSx: chipLinkLabelSx,
            onLinkClick: handleFinishSetup,
          };

    const { icon, linkLabel, onLinkClick } = chipConfig;

    return (
      <ActionChip
        icon={icon}
        backgroundColor={SEVERITY_COLORS.warning.backgroundColor}
        textColor={SEVERITY_COLORS.warning.accentColor}
        linkLabel={linkLabel}
        linkSx={chipConfig.linkSx}
        disabled={isDisabled}
        onLinkClick={onLinkClick}
      />
    );
  }

  const bannerConfig: BannerConfig =
    alertType === 'connect'
      ? {
          key: 'connect-domain',
          title: t('connectDomainTitle'),
          severity: 'info',
          icon: <InfoOutlinedIcon sx={{ color: 'info.main' }} />,
          ...SEVERITY_COLORS.info,
          actions: [
            {
              label: t('connectDomain'),
              onClick: handleConnectDomain,
              disabled: isDisabled,
              variant: 'text',
            },
          ],
        }
      : {
          key: 'finish-setup',
          title: t('finishSetupTitle'),
          severity: 'warning',
          icon: <ErrorOutlineIcon sx={{ color: 'warning.dark' }} />,
          ...SEVERITY_COLORS.warning,
          actions: [
            {
              label: t('finishSetup'),
              onClick: handleFinishSetup,
              disabled: isDisabled,
            },
          ],
        };

  const {
    title,
    severity,
    icon,
    backgroundColor,
    accentColor,
    borderColor,
    actions,
  } = bannerConfig;

  return (
    <CardTopBanner
      title={title}
      severity={severity}
      icon={icon}
      layoutBreakpoint={isListView ? 'md' : undefined}
      backgroundColor={backgroundColor}
      accentColor={accentColor}
      borderColor={borderColor}
      actions={actions}
    />
  );
};

export default SiteDomainActions;
